import { artworkValues, lumensControls, shadersMap, translateLumens, type LumensGlRunner, type LumensManifestEntry, type ShaderDef, type SketchControl } from '@flobi/artwork-core';
import { LumensGl } from '@flobi/artwork-core/lumens-gl';
import type { ShaderSource } from '@flobi/artwork-core/render';
import type * as Y from 'yjs';

/** Reads the document's shaders for the renderer. */
export function shaderSource(root: Y.Doc): ShaderSource {
  return {
    get: (shaderId) => (shadersMap(root).get(shaderId) as ShaderDef | undefined) ?? null,
  };
}

let runner: LumensGl | null | undefined;
/**
 * The one WebGL2 context Lumens shaders run in, on an OffscreenCanvas beside the canvas. Undefined
 * where there is no WebGL2: those effects then draw the layer as it is.
 */
export function lumensGlRunner(): LumensGlRunner | undefined {
  if (runner === undefined) {
    try {
      const gl = typeof OffscreenCanvas === 'undefined' ? null : new OffscreenCanvas(1, 1).getContext('webgl2', { premultipliedAlpha: false, preserveDrawingBuffer: false, antialias: false });
      runner = gl ? new LumensGl(gl) : null;
    } catch {
      runner = null;
    }
  }
  return runner ?? undefined;
}

/**
 * A Lumens effect as Lumens wrote it, to run as GL exactly as Lumens runs it: its own header,
 * passes and previous frame. Its controls and their values come from its manifest and preset.
 */
export function prepareLumensGl(item: import('../services/lumens').LibraryShaderDetail): { def: ShaderDef; values: ReturnType<typeof artworkValues>; seed: number; loop?: number } {
  const source = item.payload?.source;
  const frag = source?.frag ?? source?.fragment;
  if (!frag) throw Error('This library item is not a GLSL shader.');
  const manifest = (item.payload.manifest ?? []) as unknown as LumensManifestEntry[];
  const preset = item.payload.preset ?? {};
  const seed = Math.abs(Math.round(Number(preset.seed ?? 0))) % 2 ** 31;
  return {
    def: {
      name: item.name, sksl: '', controls: lumensControls(manifest), usesImage: true,
      gl: { frag, passes: source?.passes ?? [], manifest: manifest as unknown as Record<string, unknown>[], seed: item.payload.canvas?.seed ?? 0, fps: item.payload.canvas?.fps ?? 30, ...(item.payload.presets ? { presets: item.payload.presets } : {}) },
      origin: { app: 'lumens', itemId: item.id, ...(item.version ? { version: item.version } : {}) },
    },
    values: artworkValues(manifest, preset),
    seed,
    ...(loopOf(item.payload.canvas) ? { loop: loopOf(item.payload.canvas)! } : {}),
  };
}

/**
 * One loop of a Lumens frame, in seconds: how long its clip runs. Null for a frame that does not
 * loop (a still): nothing to play then.
 */
function loopOf(canvas: { fps?: number; durationFrames?: number; loop?: boolean } | undefined): number | null {
  if (!canvas?.durationFrames || canvas.loop === false) return null;
  const seconds = canvas.durationFrames / (canvas.fps ?? 30);
  return seconds > 0 ? Math.min(60, seconds) : null;
}

/** Translate a saved Lumens preset before changing the document. */
export function prepareLibraryShader(item: import('../services/lumens').LibraryShaderDetail): { def: ShaderDef; values: Record<string, number | number[] | boolean> } {
  const source = item.payload?.source;
  const frag = source?.frag ?? source?.fragment;
  if (source?.kind !== 'glsl' || !frag) throw Error('This library item is not a GLSL shader.');
  const translated = translateLumens({ frag, manifest: item.payload.manifest });
  if (!translated.ok || !translated.sksl) throw Error(translated.issues.find(issue => issue.severity === 'blocked')?.message ?? 'This shader cannot be imported into Artwork yet.');
  const values: Record<string, number | number[] | boolean> = {};
  for (const control of translated.controls) {
    const supplied = item.payload.preset?.[control.id] ?? control.default;
    if (typeof supplied === 'boolean' || (typeof supplied === 'number' && Number.isFinite(supplied)) || (Array.isArray(supplied) && supplied.every(n => typeof n === 'number' && Number.isFinite(n)))) values[control.id] = supplied;
    else if (control.type === 'select' && typeof supplied === 'string') {
      const options = item.payload.manifest?.find(param => param.id === control.id)?.options ?? [];
      const index = options.findIndex(option => option.value === supplied);
      if (index >= 0) values[control.id] = typeof options[index]!.value === 'number' ? options[index]!.value as number : index;
      // A blend control carries no options of its own; its choices are the blend modes, by name.
      else {
        const named = control.options?.findIndex(option => option.label === supplied) ?? -1;
        if (named >= 0) values[control.id] = control.options![named]!.value;
      }
    }
    else if (typeof supplied === 'string' && /^#[0-9a-f]{6}$/i.test(supplied)) values[control.id] = [1, 3, 5].map(at => parseInt(supplied.slice(at, at + 2), 16) / 255);
  }
  return { def: { name: item.name, sksl: translated.sksl, controls: translated.controls, usesImage: translated.usesImage, origin: { app: 'lumens', itemId: item.id } }, values };
}

/** What a sketch from the library comes in with. */
export interface LibrarySketch {
  name: string;
  code: string;
  controls: SketchControl[];
  seed: number;
  values: Record<string, unknown>;
  cover?: string;
  origin: { app: 'lumens'; itemId: string; version?: number };
  /** The looks its author saved in Lumens. */
  presets?: { name: string; seed?: number; values: Record<string, unknown> }[];
  /** What each picture control shows until one is picked: the still saved with it, by control id. */
  pictures?: Record<string, { src: string }>;
}

/**
 * Lumens' `group` entries and what the compositor applies itself are not the sketch's to read. A
 * `shape` control is: it names a layer, and the sketch gets that layer's outline (`SketchShapes`).
 * So is a `media` control: it names a picture or an artboard, and the sketch gets it drawn
 * (`SketchPictures`); and a `palette`, which is its list of colours.
 */
const NOT_SKETCH_CONTROLS = new Set(['group', 'input']);

/** Reads a saved Lumens sketch before changing the document. */
export function prepareLibrarySketch(item: import('../services/lumens').LibraryShaderDetail): LibrarySketch {
  const source = item.payload?.source;
  if (source?.kind !== 'p5' || !source.code) throw Error('This library item is not a p5 sketch.');
  const manifest = item.payload.manifest ?? [];
  const controls = manifest.filter(param => !param.channel && !NOT_SKETCH_CONTROLS.has(param.type)).slice(0, 64).map((param): SketchControl => ({
    id: param.id, label: param.label ?? '', type: param.type,
    ...(param.min !== undefined ? { min: param.min } : {}), ...(param.max !== undefined ? { max: param.max } : {}), ...(param.step !== undefined ? { step: param.step } : {}),
    ...(param.default !== undefined ? { default: param.default } : {}),
    ...(param.options ? { options: param.options.flatMap(option => option.value === undefined ? [] : [{ value: option.value, ...(option.label ? { label: option.label } : {}) }]) } : {}),
    ...(param.group ? { group: param.group } : {}),
  }));
  const preset = item.payload.preset ?? {};
  // A picture control's saved value names a layer in the Lumens frame, not one here: it starts empty, to be picked.
  const values = Object.fromEntries(controls.filter(control => control.type !== 'seed' && control.type !== 'media' && preset[control.id] !== undefined).map(control => [control.id, preset[control.id]]));
  const seedControl = controls.find(control => control.type === 'seed');
  const seed = Math.abs(Math.round(Number(seedControl ? preset[seedControl.id] ?? seedControl.default : preset.seed))) || 1;
  return { name: item.name, code: source.code, controls, seed, values, ...(item.coverUrl ? { cover: item.coverUrl } : {}), origin: { app: 'lumens', itemId: item.id, version: item.version }, ...(item.payload.presets ? { presets: item.payload.presets } : {}), ...(item.payload.pictures ? { pictures: Object.fromEntries(Object.entries(item.payload.pictures).map(([id, src]) => [id, { src }])) } : {}) };
}
